import React, { useEffect, useMemo, useState } from 'react';
import { deriveLidAantalFromStatus } from '../config/ledenUploadTemplate';

const BevestigLedenPanel = ({
  isOpen,
  leden,
  initialConfirmedLeden,
  gemeenschapNaam,
  isGuestGroup = false,
  conviventieNaam,
  conviventieStartDate,
  conviventieEndDate,
  uploadedAt,
  uploadedBy,
  submitting = false,
  onManageLeden,
  onSubmit,
  onClose
}) => {
  const [memberRows, setMemberRows] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    const existingById = new Map(
      (initialConfirmedLeden || []).map((lid) => [lid.lidId || lid.id, lid])
    );

    setMemberRows(
      (leden || []).map((lid) => {
        const existing = existingById.get(lid.id) || {};

        return {
          id: lid.id,
          voornaam: lid.voornaam || '',
          achternaam: lid.achternaam || '',
          status: lid.status || '',
          aantal: deriveLidAantalFromStatus(lid.status),
          geboortedatum: existing.geboortedatum || lid.geboortedatum || '',
          email: existing.email || lid.email || '',
          telefoon: existing.telefoon || lid.telefoon || '',
          confirmed: !!existingById.get(lid.id),
          bijzonderheden: existing.bijzonderheden || '',
          aankomst: existing.aankomst || '',
          vertrek: existing.vertrek || ''
        };
      })
    );
  }, [isOpen, initialConfirmedLeden, leden]);

  const confirmedLedenCount = useMemo(
    () => memberRows
      .filter((row) => row.confirmed)
      .reduce((sum, row) => sum + deriveLidAantalFromStatus(row.status), 0),
    [memberRows]
  );

  const formatUploadedAt = (value) => {
    if (!value) return '-';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const updateRow = (id, field, value) => {
    setMemberRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, [field]: value } : row
    )));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedRows = memberRows.filter((row) => row.confirmed);

    for (const row of selectedRows) {
      if (row.aankomst && conviventieStartDate && row.aankomst < conviventieStartDate) {
        alert(`Aankomst mag niet voor de start datum liggen voor ${row.voornaam} ${row.achternaam}.`);
        return;
      }

      if (row.vertrek && conviventieEndDate && row.vertrek > conviventieEndDate) {
        alert(`Vertrek mag niet na de eind datum liggen voor ${row.voornaam} ${row.achternaam}.`);
        return;
      }

      if (row.aankomst && row.vertrek && row.aankomst >= row.vertrek) {
        alert(`Aankomst moet voor vertrek liggen voor ${row.voornaam} ${row.achternaam}.`);
        return;
      }
    }

    const selectedLeden = selectedRows.map((row) => ({
      id: row.id,
      voornaam: row.voornaam,
      achternaam: row.achternaam,
      status: row.status,
      aantal: deriveLidAantalFromStatus(row.status),
      geboortedatum: row.geboortedatum || '',
      email: row.email || '',
      telefoon: row.telefoon || '',
      confirmed: row.confirmed,
      bijzonderheden: row.bijzonderheden,
      aankomst: row.aankomst,
      vertrek: row.vertrek
    }));

    onSubmit(selectedLeden);
  };

  return (
    <section className="confirm-inline-panel" aria-label="Bevestig leden panel">
      <div className="confirm-inline-header">
        <div>
          <h3>Bevestig deelname - {gemeenschapNaam}</h3>
          <p>
            {conviventieNaam} | {conviventieStartDate || '-'} t/m {conviventieEndDate || '-'}
          </p>
        </div>
        <div className="confirm-inline-header-right">
          <span>Bevestigd: {confirmedLedenCount}</span>
          <button type="button" className="button button-secondary" onClick={onClose}>
            Sluiten
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="upload-info-banner">
          Ledenlijst geupload op {formatUploadedAt(uploadedAt)} door {uploadedBy || '-'}.
        </div>

        {memberRows.length === 0 ? (
          <div className="confirm-empty-state">
            <p>
              Nog geen leden in deze {isGuestGroup ? 'gastengroep' : 'gemeenschap'}. Upload eerst een ledenlijst via de knop &quot;Ledenlijst uploaden&quot;.
            </p>
          </div>
        ) : (
          <div className="confirm-table-wrapper">
            <table className="confirm-leden-table">
              <thead>
                <tr>
                  <th>Bevestigd</th>
                  <th>Voornaam</th>
                  <th>Achternaam</th>
                  <th>Status</th>
                  <th>Aantal</th>
                  <th>Aankomst</th>
                  <th>Vertrek</th>
                  <th>Bijzonderheden</th>
                </tr>
              </thead>
              <tbody>
                {memberRows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Bevestigd" className="field-confirm">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label="Bevestigd"
                          aria-checked={row.confirmed}
                          checked={row.confirmed}
                          onChange={(e) => updateRow(row.id, 'confirmed', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td data-label="Voornaam" className="field-firstname">{row.voornaam || '-'}</td>
                    <td data-label="Achternaam" className="field-lastname">{row.achternaam || '-'}</td>
                    <td data-label="Status" className="field-status">{row.status || '-'}</td>
                    <td data-label="Aantal" className="field-count">{row.aantal}</td>
                    <td data-label="Aankomst" className="field-arrival">
                      <div className="date-input-group">
                        <input
                          type="date"
                          aria-label="Aankomst"
                          className={row.aankomst ? '' : 'date-empty'}
                          value={row.aankomst}
                          min={conviventieStartDate || undefined}
                          max={conviventieEndDate || undefined}
                          onChange={(e) => updateRow(row.id, 'aankomst', e.target.value)}
                        />
                        <button
                          type="button"
                          className="quick-date-btn"
                          title={conviventieStartDate ? `Stel standaard startdatum in (${conviventieStartDate})` : 'Geen startdatum ingesteld'}
                          aria-label={`Stel standaard startdatum in voor ${row.voornaam || ''} ${row.achternaam || ''}`.trim() || 'Stel standaard startdatum in'}
                          disabled={!conviventieStartDate}
                          onClick={() => updateRow(row.id, 'aankomst', conviventieStartDate)}
                        >
                          📅
                        </button>
                      </div>
                    </td>
                    <td data-label="Vertrek" className="field-departure">
                      <div className="date-input-group">
                        <input
                          type="date"
                          aria-label="Vertrek"
                          className={row.vertrek ? '' : 'date-empty'}
                          value={row.vertrek}
                          min={conviventieStartDate || undefined}
                          max={conviventieEndDate || undefined}
                          onChange={(e) => updateRow(row.id, 'vertrek', e.target.value)}
                        />
                        <button
                          type="button"
                          className="quick-date-btn"
                          title={conviventieEndDate ? `Stel standaard einddatum in (${conviventieEndDate})` : 'Geen einddatum ingesteld'}
                          aria-label={`Stel standaard einddatum in voor ${row.voornaam || ''} ${row.achternaam || ''}`.trim() || 'Stel standaard einddatum in'}
                          disabled={!conviventieEndDate}
                          onClick={() => updateRow(row.id, 'vertrek', conviventieEndDate)}
                        >
                          📅
                        </button>
                      </div>
                    </td>
                    <td data-label="Bijzonderheden" className="field-notes">
                      <input
                        type="text"
                        aria-label="Bijzonderheden"
                        value={row.bijzonderheden}
                        onChange={(e) => updateRow(row.id, 'bijzonderheden', e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="confirm-inline-actions">
          {onManageLeden && (
            <button
              type="button"
              className="button button-secondary confirm-manage-btn"
              onClick={onManageLeden}
            >
              👥 Leden beheren
            </button>
          )}
          <button type="button" className="button button-secondary" onClick={onClose}>
            {memberRows.length === 0 ? 'Sluiten' : 'Annuleren'}
          </button>
          {memberRows.length > 0 && (
            <button
              type="submit"
              className="button button-primary"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Bevestigen…' : 'Bevestigen'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default BevestigLedenPanel;
